/**
 * ContratoPDF.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Contrato de prestación de servicios para eventos de A la Vuelta 360°.
 * Utiliza @react-pdf/renderer para generar un PDF imprimible con datos del cliente,
 * domicilio, servicio contratado, cláusulas de obligaciones y penalizaciones.
 */
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

const F = "Helvetica";
const FB = "Helvetica-Bold";

const C = {
  white: "#FFFFFF",
  black: "#111827",
  brand: "#1A1A2E",
  accent: "#D67357",
  accentLight: "#F7E4DF",
  gray: "#4B5563",
  grayLight: "#9CA3AF",
  border: "#D1D5DB",
  bg: "#F9FAFB",
};

const S = StyleSheet.create({
  page: {
    fontFamily: F,
    fontSize: 10,
    lineHeight: 1.4,
    paddingTop: 30,
    paddingBottom: 40,
    paddingHorizontal: 40,
    backgroundColor: C.white,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },
  logoBox: {
    width: 70,
    height: 70,
    overflow: "hidden",
    borderRadius: 35,
    borderWidth: 1,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  logoImage: {
    width: 70,
    height: 70,
  },
  brandName: {
    fontFamily: FB,
    fontSize: 18,
    color: C.brand,
    letterSpacing: 1,
  },
  subtitle: {
    fontFamily: F,
    fontSize: 8,
    color: C.gray,
    marginTop: 3,
  },
  title: {
    fontFamily: FB,
    fontSize: 14,
    color: C.white,
    backgroundColor: C.brand,
    paddingVertical: 8,
    paddingHorizontal: 10,
    alignSelf: "flex-start",
    borderRadius: 4,
    letterSpacing: 1,
    marginBottom: 18,
  },
  section: {
    marginBottom: 12,
  },
  sectionHeader: {
    fontFamily: FB,
    fontSize: 10,
    color: C.brand,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  text: {
    fontFamily: F,
    fontSize: 9,
    color: C.black,
    textAlign: "justify",
  },
  bold: {
    fontFamily: FB,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 6,
  },
  infoBox: {
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 6,
    backgroundColor: C.bg,
    padding: 10,
    marginBottom: 10,
  },
  infoLabel: {
    fontFamily: FB,
    fontSize: 8,
    color: C.brand,
    marginBottom: 2,
  },
  infoValue: {
    fontFamily: F,
    fontSize: 9,
    color: C.black,
  },
  clause: {
    marginBottom: 10,
  },
  clauseTitle: {
    fontFamily: FB,
    fontSize: 9,
    color: C.brand,
    marginBottom: 3,
  },
  clauseText: {
    fontFamily: F,
    fontSize: 8.7,
    color: C.black,
    textAlign: "justify",
  },
  listItem: {
    marginBottom: 4,
    paddingLeft: 10,
  },
  listBullet: {
    fontFamily: FB,
  },
  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 20,
    marginTop: 14,
  },
  signatureBox: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: C.grayLight,
    paddingTop: 6,
  },
  signatureLabel: {
    fontFamily: F,
    fontSize: 8,
    color: C.gray,
  },
  pageFooter: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8,
    color: C.gray,
  },
  pageNumber: {
    textAlign: "right",
    width: 100,
  },
});

const fmtDate = (value) => {
  if (!value) return "—";
  const date = new Date(value.includes("T") ? value : `${value}T12:00:00`);
  return date.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const fmtTime = (value) => {
  if (!value) return "—";
  const [hour, minute] = String(value).split(":").map(Number);
  if (Number.isNaN(hour)) return value;
  const ampm = hour >= 12 ? "p.m." : "a.m.";
  return `${hour % 12 || 12}:${String(minute || 0).padStart(2, "0")} ${ampm}`;
};

const fmtMoney = (value) => {
  const amount = parseFloat(value) || 0;
  return `$${amount.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safeText = (value) => (value || "—");

const ContratoPDF = ({ evento, logoUrl }) => {
  if (!evento) return null;

  const clienteNombre = safeText(evento.cliente_nombre || evento.cliente?.nombre_completo);
  const clienteTelefono = safeText(evento.cliente_telefono || evento.telefono || evento.cliente?.telefono);
  const clienteDomicilio = safeText(evento.cliente_direccion || evento.direccion || evento.ubicacion || evento.cliente?.domicilio);
  const servicio = safeText(evento.paquete_nombre || evento.tipo_servicio_nombre || evento.titulo);
  const descripcion = safeText(evento.descripcion);
  const fecha = fmtDate(evento.fecha_evento || evento.fecha_inicio);
  const hora = fmtTime(evento.hora_inicio);
  const lugar = safeText(evento.lugar_evento || evento.ubicacion || evento.direccion);
  const costo = fmtMoney(evento.costo_servicio || evento.monto_paquete || "0");
  const anticipo = fmtMoney(evento.anticipo || evento.adelanto || "0");
  const restante = fmtMoney((parseFloat(evento.costo_servicio || evento.monto_paquete || 0) - parseFloat(evento.anticipo || evento.adelanto || 0)) || 0);
  const contratoNumero = evento.id ? `CT-${String(evento.id).padStart(5, "0")}` : "CT-00000";
  const fechaGenerado = fmtDate(new Date().toISOString());

  return (
    <Document
      title={`Contrato-${contratoNumero}-${clienteNombre.replace(/\s+/g, "_")}`}
      author="A la Vuelta 360°"
      subject="Contrato de prestación de servicios"
      creator="Agenda A la Vuelta 360°"
    >
      <Page size="A4" style={S.page}>
        <View style={S.header}>
          <View>
            <Text style={S.brandName}>A LA VUELTA 360°</Text>
            <Text style={S.subtitle}>Contrato de prestación de servicios para eventos</Text>
          </View>
          {logoUrl ? (
            <View style={S.logoBox}>
              <Image src={logoUrl} style={S.logoImage} />
            </View>
          ) : null}
        </View>

        <Text style={S.title}>CONTRATO DE PRESTACIÓN DE SERVICIOS</Text>

        <View style={S.section}>
          <Text style={S.sectionHeader}>Datos del contrato</Text>
          <View style={S.infoBox}>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Número de contrato: </Text>
              {contratoNumero}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Fecha de emisión: </Text>
              {fechaGenerado}
            </Text>
          </View>
        </View>

        <View style={S.section}>
          <Text style={S.sectionHeader}>Datos del cliente</Text>
          <View style={S.infoBox}>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Nombre completo: </Text>
              {clienteNombre}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Teléfono / contacto: </Text>
              {clienteTelefono}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Domicilio / ubicación: </Text>
              {clienteDomicilio}
            </Text>
          </View>
        </View>

        <View style={S.section}>
          <Text style={S.sectionHeader}>Datos del servicio</Text>
          <View style={S.infoBox}>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Servicio o paquete contratado: </Text>
              {servicio}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Descripción del servicio: </Text>
              {descripcion}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Fecha y hora del evento: </Text>
              {fecha} · {hora}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Lugar de prestación: </Text>
              {lugar}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Monto total: </Text>
              {costo}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Anticipo acordado: </Text>
              {anticipo}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Saldo restante: </Text>
              {restante}
            </Text>
          </View>
        </View>

        <View style={S.section}>
          <Text style={S.sectionHeader}>Cláusulas principales</Text>
          <View style={S.clause}>
            <Text style={S.clauseTitle}>1. Objeto</Text>
            <Text style={S.clauseText}>
              El presente contrato tiene por objeto la prestación del servicio de
              organización, coordinación y ejecución del evento descrito, por parte
              de A la Vuelta 360° ("El Prestador"), para el cliente identificado
              en este documento ("El Cliente"). El servicio deberá ajustarse a los
              datos, alcances y condiciones pactadas en el presente contrato.
            </Text>
          </View>

          <View style={S.clause}>
            <Text style={S.clauseTitle}>2. Alcance del servicio</Text>
            <Text style={S.clauseText}>
              El Prestador realizará las actividades necesarias para brindar el
              servicio contratado, incluyendo la logística, montaje, operación y
              supervisión del evento. Cualquier servicio adicional o cambio de
              alcance deberá documentarse y aprobarse por escrito entre las partes.
            </Text>
          </View>

          <View style={S.clause}>
            <Text style={S.clauseTitle}>3. Obligaciones del Prestador</Text>
            <Text style={S.clauseText}>
              Son obligaciones del Prestador:
            </Text>
            <Text style={S.listItem}>• Entregar el servicio contratado con calidad y puntualidad.</Text>
            <Text style={S.listItem}>• Contar con el personal, equipo y materiales adecuados.</Text>
            <Text style={S.listItem}>• Notificar cualquier cambio relevante con al menos 48 horas.</Text>
            <Text style={S.listItem}>• Respetar la normatividad vigente y las políticas del lugar del evento.</Text>
          </View>

          <View style={S.clause}>
            <Text style={S.clauseTitle}>4. Obligaciones del Cliente</Text>
            <Text style={S.clauseText}>
              Son obligaciones del Cliente:
            </Text>
            <Text style={S.listItem}>• Proporcionar la información, accesos y autorizaciones necesarias.</Text>
            <Text style={S.listItem}>• Realizar los pagos en las fechas acordadas.</Text>
            <Text style={S.listItem}>• Facilitar condiciones seguras y legales para el desarrollo del evento.</Text>
            <Text style={S.listItem}>• Colaborar en la coordinación de horarios, logística y requerimientos.</Text>
          </View>

          <View style={S.clause}>
            <Text style={S.clauseTitle}>5. Pagos y condiciones</Text>
            <Text style={S.clauseText}>
              El monto total del servicio es el indicado en este contrato. El
              anticipo se paga en la fecha acordada para confirmar la reserva.
              El saldo restante deberá pagarse conforme a lo establecido en la
              propuesta comercial, preferentemente antes del inicio del evento.
            </Text>
          </View>

          <View style={S.clause}>
            <Text style={S.clauseTitle}>6. Cambios y cancelaciones</Text>
            <Text style={S.clauseText}>
              Cualquier modificación del servicio deberá confirmarse por escrito.
              En caso de cancelación del evento por parte del Cliente, se aplicarán
              las penalizaciones establecidas en la cláusula siguiente.
            </Text>
          </View>

          <View style={S.clause}>
            <Text style={S.clauseTitle}>7. Penalizaciones</Text>
            <Text style={S.clauseText}>
              En caso de incumplimiento de las obligaciones de cualquiera de las
              partes, se aplicarán las siguientes penalizaciones:
            </Text>
            <Text style={S.listItem}>• Cliente: cancelación con más de 15 días de anticipación: 10% del total.</Text>
            <Text style={S.listItem}>• Cliente: cancelación entre 7 y 14 días antes: 30% del total.</Text>
            <Text style={S.listItem}>• Cliente: cancelación entre 72 horas y 7 días antes: 50% del total.</Text>
            <Text style={S.listItem}>• Cliente: cancelación dentro de las 72 horas antes del evento o ausencia: 70% del total.</Text>
            <Text style={S.listItem}>• Prestador: incumplimiento en la prestación del servicio sin causa justificada: devolución parcial o reposición de servicio.</Text>
            <Text style={S.listItem}>• Prestador: baja calidad o falta de equipo en la fecha acordada: compensación proporcional, hasta un 20% del monto total.</Text>
          </View>
        </View>
      </Page>

      <Page size="A4" style={S.page}>
        <View style={S.section}>
          <Text style={S.sectionHeader}>8. Responsabilidad</Text>
          <Text style={S.clauseText}>
            El Prestador será responsable por el cumplimiento de los servicios
            contratados y por los daños directos ocasionados por su propia
            actuación. No será responsable por fallas derivadas de terceros,
            caso fortuito, fuerza mayor, condiciones del recinto o cambios de
            último minuto impuestos por el Cliente o autoridades.
          </Text>
        </View>

        <View style={S.section}>
          <Text style={S.sectionHeader}>9. Confidencialidad</Text>
          <Text style={S.clauseText}>
            Ambas partes acuerdan mantener confidencial la información técnica,
            comercial o personal compartida para la organización del evento.
            Solo se podrá divulgar la información cuando exista autorización
            expresa o obligación legal.
          </Text>
        </View>

        <View style={S.section}>
          <Text style={S.sectionHeader}>10. Jurisdicción y ley aplicable</Text>
          <Text style={S.clauseText}>
            Este contrato se regirá por las leyes aplicables de los Estados
            Unidos Mexicanos y, en lo local, por las disposiciones del Estado
            de México. Para la solución de controversias, las partes se someten
            a los tribunales competentes del municipio correspondiente.
          </Text>
        </View>

        <View style={S.section}>
          <Text style={S.sectionHeader}>11. Datos de la prestación</Text>
          <View style={S.infoBox}>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Evento: </Text>
              {safeText(evento.titulo)}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Ubicación del evento: </Text>
              {lugar}
            </Text>
            <Text style={S.infoValue}>
              <Text style={S.infoLabel}>Servicios adicionales / notas: </Text>
              {safeText(evento.notas || evento.servicios_adicionales?.map((item) => item.nombre).join(", ") || "—")}
            </Text>
          </View>
        </View>

        <View style={S.section}>
          <Text style={S.clauseTitle}>12. Aceptación</Text>
          <Text style={S.clauseText}>
            Con la firma del presente documento, ambas partes aceptan las
            condiciones establecidas en este contrato y manifiestan que los
            datos proporcionados son verídicos. Las partes declaran tener la
            capacidad legal necesaria para celebrar este contrato.
          </Text>
        </View>

        <View style={S.signatureRow}>
          <View style={S.signatureBox}>
            <Text style={S.signatureLabel}>Firma del Cliente</Text>
          </View>
          <View style={S.signatureBox}>
            <Text style={S.signatureLabel}>Firma del Prestador</Text>
          </View>
        </View>

        <View style={S.pageFooter}>
          <Text>A la Vuelta 360° | Contrato de prestación de servicios</Text>
          <Text style={S.pageNumber}>Página 2 de 2</Text>
        </View>
      </Page>
    </Document>
  );
};

export default ContratoPDF;
